import { useState, useCallback } from 'react'
import { supabase } from '../lib/supabase'
import type { OrganizationApp } from '../types/database'

interface UseOrgAppsReturn {
  apps: OrganizationApp[]
  loading: boolean
  error: string | null
  loadApps: (organizationId: string) => Promise<void>
  toggleApp: (organizationId: string, appId: string, enabled: boolean) => Promise<boolean>
  refresh: () => Promise<void>
}

export function useOrgApps(): UseOrgAppsReturn {
  const [apps, setApps] = useState<OrganizationApp[]>([])
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const [currentOrgId, setCurrentOrgId] = useState<string | null>(null)

  const loadApps = useCallback(async (organizationId: string) => {
    try {
      setLoading(true)
      setError(null)
      setCurrentOrgId(organizationId)

      const { data, error: fetchError } = await supabase.rpc('get_org_apps', {
        p_organization_id: organizationId
      })

      if (fetchError) throw fetchError

      setApps(data || [])
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to load apps')
    } finally {
      setLoading(false)
    }
  }, [])

  const toggleApp = async (organizationId: string, appId: string, enabled: boolean): Promise<boolean> => {
    try {
      setError(null)

      const { error: toggleError } = await supabase.rpc('toggle_org_app', {
        p_organization_id: organizationId,
        p_app_id: appId,
        p_enabled: enabled
      })

      if (toggleError) throw toggleError

      // Update local state
      setApps(prev =>
        prev.map(app =>
          app.app_id === appId
            ? { ...app, is_enabled: enabled, enabled_at: enabled ? new Date().toISOString() : app.enabled_at }
            : app
        )
      )

      return true
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to toggle app')
      return false
    }
  }

  const refresh = useCallback(async () => {
    if (currentOrgId) {
      await loadApps(currentOrgId)
    }
  }, [currentOrgId, loadApps])

  return {
    apps,
    loading,
    error,
    loadApps,
    toggleApp,
    refresh
  }
}
